import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import type { OrderDetailResponseDTO } from '@libs/dtos/order/order.dto';
import { CurrentUserService } from '@services/auth/current-user.service';
import { OrdersService } from '@services/orders/orders.service';
import { ButtonComponent } from '@shared/components/button/button.component';
import { SpinnerComponent } from '@shared/components/spinner/spinner.component';
import type { TagData } from '@shared/components/table/table.model';
import { TagComponent } from '@shared/components/tag/tag.component';
import { SharedModule } from '@shared/modules/shared.module';
import { activeLocale, formatDateTime, formatEur } from 'app/core/utils/locale';
import { paymentStatusTag, providerStatusTag } from '../order-status';

/** Answers meaning "this order is not available to you" (no retry offered) */
const NOT_FOUND_STATUSES = [400, 403, 404];

/** Brand names are shown as they are; only the card method needs a translation. */
const PAYMENT_METHOD_LABELS: Record<string, string> = {
	REVOLUT: 'Revolut',
	BIZUM: 'Bizum',
	BTC: 'Bitcoin',
	USDT_TRC20: 'USDT (TRC20)',
	USDT_ERC20: 'USDT (ERC20)',
};

@Component({
	selector: 'app-order-detail',
	imports: [SharedModule, ButtonComponent, SpinnerComponent, TagComponent],
	templateUrl: './order-detail.component.html',
	styleUrl: './order-detail.component.scss',
})
export class OrderDetailComponent implements OnInit {
	order: OrderDetailResponseDTO | null = null;
	loading = true;
	notFound = false;
	loadError = false;
	/** Internal Stripe ids are only useful (and shown) to admins */
	readonly isAdmin: boolean;
	private orderId: string | null = null;

	paymentTag: TagData | null = null;
	providerTag: TagData | null = null;

	constructor(
		private route: ActivatedRoute,
		private router: Router,
		private ordersService: OrdersService,
		private translocoService: TranslocoService,
		currentUserService: CurrentUserService,
	) {
		this.isAdmin = currentUserService.hasPermission('orders:view-all');
	}

	ngOnInit(): void {
		this.orderId = this.route.snapshot.paramMap.get('id');
		if (!this.orderId) {
			this.router.navigate(['/orders']);
			return;
		}
		this.loadOrder();
	}

	loadOrder(): void {
		if (!this.orderId) return;
		this.loading = true;
		this.notFound = false;
		this.loadError = false;

		this.ordersService.getOrderById(this.orderId).subscribe({
			next: (order) => {
				this.order = order;
				this.paymentTag = paymentStatusTag(order.paymentStatus, this.translocoService);
				// The backend strips provider data for clients
				this.providerTag = order.providerStatus
					? providerStatusTag(order.providerStatus, this.translocoService)
					: null;
				this.loading = false;
			},
			error: (err: unknown) => {
				// 400 (bad id), 403 (not yours) and 404 (does not exist): retrying will not change it, show "not found".
				// Network errors and 5xx are load failures worth retrying
				const status = err instanceof HttpErrorResponse ? err.status : 0;
				if (NOT_FOUND_STATUSES.includes(status)) this.notFound = true;
				else this.loadError = true;
				this.loading = false;
			},
		});
	}

	get isCrypto(): boolean {
		return !!this.order?.cryptoCurrency;
	}

	get hasDiscount(): boolean {
		return !!this.order?.discountId || Number(this.order?.discountAmount) > 0;
	}

	get userName(): string {
		const user = this.order?.user;
		return user ? `${user.name ?? ''} ${user.lastName ?? ''}`.trim() || '-' : '-';
	}

	get paymentMethodLabel(): string {
		const method = this.order?.paymentMethod;
		if (!method) return '-';
		if (method === 'STRIPE') return this.translocoService.translate('orders.paymentMethods.STRIPE');
		return PAYMENT_METHOD_LABELS[method] || method;
	}

	money(value: unknown): string {
		return formatEur(value, activeLocale(this.translocoService));
	}

	dateTime(value: string | null | undefined): string {
		return formatDateTime(value, activeLocale(this.translocoService));
	}
}
